import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import { onAuthStateChanged, signInWithCustomToken, getIdTokenResult, type User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';

export interface LabOSUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: string;
  isAdmin: boolean;
}

interface LabOSState {
  user: LabOSUser | null;
  firebaseUser: User | null;
  config: Record<string, unknown>;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<LabOSState | null>(null);
const LABOS_URL = 'https://labos.wbdcontentlab.dev';

export function LabOSAuth({ children, appId, fallback }: { children: ReactNode; appId: string; fallback?: ReactNode }) {
  const [state, setState] = useState<Omit<LabOSState, 'signOut'>>({
    user: null,
    firebaseUser: null,
    config: {},
    loading: true,
  });
  const isHandshakeCodeProccessing = useRef(false);

  useEffect(() => {
    // Check for handshake code in URL (redirect from LabOS)
    const params = new URLSearchParams(window.location.search);
    const handshakeCode = params.get('handshake');

    if (handshakeCode) {
      // Redeem the handshake code for a Firebase custom token
      const redeem = httpsCallable<{ code: string }, { customToken: string }>(functions, 'redeemHandshakeCode');
      isHandshakeCodeProccessing.current = true;
      redeem({ code: handshakeCode })
        .then(async (result) => {
          await signInWithCustomToken(auth, result.data.customToken);
        })
        .catch((err) => {
          console.error('[LabOS] Handshake redemption failed:', err);
        })
        .finally(() => {
          isHandshakeCodeProccessing.current = false;
          // Clean up URL
          params.delete('handshake');
          params.delete('origin');
          const clean = window.location.pathname + (params.toString() ? '?' + params : '');
          window.location.href = clean;
        });
    }

    return onAuthStateChanged(auth, async (fbUser) => {
      if (isHandshakeCodeProccessing.current) return;
      if (!fbUser) {
        // Not authenticated — redirect to LabOS
        const returnUrl = encodeURIComponent(window.location.href);
        window.location.href = `${LABOS_URL}/login?return=${returnUrl}&app=${appId}`;
        return;
      }

      const token = await getIdTokenResult(fbUser);
      const role = (token.claims.role as string) || 'user';

      // Fetch runtime config from LabOS (group-level app parameters)
      let config: Record<string, unknown> = {};
      try {
        const getAppConfig = httpsCallable<{ appId: string }, Record<string, unknown>>(functions, 'getAppConfig');
        const result = await getAppConfig({ appId });
        config = result.data || {};
      } catch {
        /* config is optional */
      }

      setState({
        user: {
          uid: fbUser.uid,
          email: fbUser.email,
          displayName: fbUser.displayName,
          role,
          isAdmin: role === 'admin',
        },
        firebaseUser: fbUser,
        config,
        loading: false,
      });
    });
  }, [appId]);

  const signOut = async () => {
    await auth.signOut();
    window.location.href = LABOS_URL;
  };

  if (state.loading) {
    return (
      <>
        {fallback || (
          <div
            style={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fff',
              color: 'rgba(0,0,0,0.3)',
              fontSize: '14px',
              fontFamily: 'system-ui',
            }}
          >
            Authenticating...
          </div>
        )}
      </>
    );
  }

  return <Ctx.Provider value={{ ...state, signOut }}>{children}</Ctx.Provider>;
}

// Simple mock for development - provides context without errors
export function LabOSAuthMock({ children }: { children: ReactNode }) {
  const mockState: LabOSState = {
    user: null,
    firebaseUser: null,
    config: {},
    loading: false,
    signOut: async () => {
      console.log('[LabOS Mock] Sign out called');
    },
  };

  return <Ctx.Provider value={mockState}>{children}</Ctx.Provider>;
}

export function useLabOS(): LabOSState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useLabOS() must be inside <LabOSAuth>');
  return ctx;
}

export function LabOSTopbar({ appName, accentColor }: { appName: string; accentColor?: string }) {
  const { user, signOut } = useLabOS();
  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '40px',
        padding: '0 16px',
        background: '#fff',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        fontFamily: 'system-ui',
        fontSize: '12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <a
          href={LABOS_URL}
          style={{ color: 'rgba(0,0,0,0.35)', textDecoration: 'none', fontWeight: 500, letterSpacing: '0.05em' }}
        >
          LabOS
        </a>
        <span style={{ color: 'rgba(0,0,0,0.12)' }}>/</span>
        <span style={{ color: accentColor || 'rgba(0,0,0,0.8)', fontWeight: 600 }}>{appName}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ color: 'rgba(0,0,0,0.3)' }}>{user?.email}</span>
        <button
          onClick={signOut}
          style={{
            background: 'none',
            border: '1px solid rgba(0,0,0,0.08)',
            color: 'rgba(0,0,0,0.4)',
            padding: '2px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px',
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
